import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Alert, AlertFormData, SearchResult, PriceAnalysis, SelectedBrand, SelectedSize, SelectedMaterial, SelectedPattern, SelectedLanguage, SelectedPlatform, SelectedRating } from '../types/alert.types';
import { Step1Name } from '../components/AlertModal/Step1Name';
import { Step2Url } from '../components/AlertModal/Step2Url';
import { Step3Category } from '../components/AlertModal/Step3Category';
import { Step4CategoryFilters } from '../components/AlertModal/Step4CategoryFilters';
import { Step5GeneralOptions } from '../components/AlertModal/Step5GeneralOptions';
import { FilterBadgesSummary } from '../components/FilterBadgesSummary';
import { getColorByTitle } from '../utils/vintedColors';

interface EditAlertViewProps {
  alert: Alert;
  onBack: () => void;
  onSave: (formData: AlertFormData) => void;
  results: SearchResult[];
  analysis: PriceAnalysis | null;
  isUnlimited?: boolean;
  userPlan?: string;
}

export const EditAlertView: React.FC<EditAlertViewProps> = ({
  alert,
  onBack,
  onSave,
  results,
  analysis,
  isUnlimited = false,
  userPlan = 'free',
}) => {
  // Gérer la migration des anciennes données (strings) vers les nouvelles (objets {id, title})
  const brands = alert.selected_brands || [];
  const selectedBrands: SelectedBrand[] = brands.map((brand: any) => {
    // Si c'est déjà un objet avec id et title, le garder tel quel
    if (typeof brand === 'object' && brand.id && brand.title) {
      return brand as SelectedBrand;
    }
    // Si c'est une string (anciennes données), créer un objet temporaire avec id = 0
    return { id: 0, title: brand as string };
  });

  // Gérer la migration des tailles (strings) vers les nouvelles (objets {id, title})
  const sizes = alert.selected_sizes || [];
  const selectedSizes: SelectedSize[] = sizes.map((size: any) => {
    if (typeof size === 'object' && size.id !== undefined && size.title) {
      return size as SelectedSize;
    }
    return { id: 0, title: size as string };
  });

  // Gérer la migration des matières
  const materials = alert.selected_materials || [];
  const selectedMaterials: SelectedMaterial[] = materials.map((material: any) => {
    if (typeof material === 'object' && material.id !== undefined && material.title) {
      return material as SelectedMaterial;
    }
    return { id: material as number, title: `Matière ${material}` };
  });

  // Gérer la migration des motifs
  const patterns = alert.selected_patterns || [];
  const selectedPatterns: SelectedPattern[] = patterns.map((pattern: any) => {
    if (typeof pattern === 'object' && pattern.id !== undefined && pattern.title) {
      return pattern as SelectedPattern;
    }
    return { id: pattern as number, title: `Motif ${pattern}` };
  });

  // Gérer la migration des langues
  const languages = alert.selected_languages || [];
  const selectedLanguages: SelectedLanguage[] = languages.map((language: any) => {
    if (typeof language === 'object' && language.id && language.title) {
      return language as SelectedLanguage;
    }
    return { id: language as string, title: `Langue ${language}` };
  });

  // Gérer la migration des plateformes
  const platforms = alert.selected_platforms || [];
  const selectedPlatforms: SelectedPlatform[] = platforms.map((platform: any) => {
    if (typeof platform === 'object' && platform.id !== undefined && platform.title) {
      return platform as SelectedPlatform;
    }
    return { id: platform as number, title: `Plateforme ${platform}` };
  });

  // Gérer la migration des ratings
  const ratings = alert.selected_ratings || [];
  const selectedRatings: SelectedRating[] = ratings.map((rating: any) => {
    if (typeof rating === 'object' && rating.id !== undefined && rating.title) {
      return rating as SelectedRating;
    }
    return { id: rating as number, title: `Rating ${rating}` };
  });

  const [formData, setFormData] = useState<AlertFormData>({
    name: alert.name,
    vintedUrl: alert.vinted_url || '',
    searchText: '',
    categoryId: alert.category_id || '',
    categoryPath: alert.category_path || '',
    selectedConditions: alert.selected_conditions || [],
    selectedSizes: selectedSizes,
    selectedColors: alert.selected_colors || [],
    selectedBrands: selectedBrands,
    selectedStatuses: alert.selected_statuses || [],
    selectedLanguages: selectedLanguages,
    selectedMaterials: selectedMaterials,
    selectedPatterns: selectedPatterns,
    selectedPlatforms: selectedPlatforms,
    selectedRatings: selectedRatings,
    minPrice: alert.min_price?.toString() || '',
    maxPrice: alert.max_price?.toString() || '',
    minLikes: alert.min_likes?.toString() || '',
    maxLikes: alert.max_likes?.toString() || '',
    maxPages: alert.max_pages.toString(),
    trendingOnly: alert.trending_only || false,
    mustInclude: alert.must_include || [],
    mustExclude: alert.must_exclude || []
  });

  const handleSave = () => {
    onSave(formData);
  };

  const updateField = <K extends keyof AlertFormData>(field: K, value: AlertFormData[K]) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Si les marques changent, mettre à jour l'URL avec brand_ids (sans [])
      if (field === 'selectedBrands' && newData.vintedUrl) {
        const brands = value as SelectedBrand[];
        try {
          const urlObj = new URL(newData.vintedUrl);
          const params = new URLSearchParams(urlObj.search);

          // Supprimer tous les anciens paramètres brand_ids et brand_ids[]
          const paramsArray: [string, string][] = [];
          params.forEach((val, key) => {
            if (key !== 'brand_ids[]' && key !== 'brand_ids') {
              paramsArray.push([key, val]);
            }
          });

          // Ajouter les nouveaux paramètres brand_ids (sans [])
          brands.forEach(brand => {
            paramsArray.push(['brand_ids', brand.id.toString()]);
          });

          urlObj.search = new URLSearchParams(paramsArray).toString();
          newData.vintedUrl = urlObj.toString();
        } catch (e) {
          console.error('Erreur lors de la mise à jour de l\'URL:', e);
        }
      }

      // Si les états changent, mettre à jour l'URL avec status_ids (sans [])
      if (field === 'selectedStatuses' && newData.vintedUrl) {
        const statuses = value as number[];
        try {
          const urlObj = new URL(newData.vintedUrl);
          const params = new URLSearchParams(urlObj.search);

          // Supprimer tous les anciens paramètres status_ids et status_ids[]
          const paramsArray: [string, string][] = [];
          params.forEach((val, key) => {
            if (key !== 'status_ids[]' && key !== 'status_ids') {
              paramsArray.push([key, val]);
            }
          });

          // Ajouter les nouveaux paramètres status_ids (sans [])
          statuses.forEach(statusId => {
            paramsArray.push(['status_ids', statusId.toString()]);
          });

          urlObj.search = new URLSearchParams(paramsArray).toString();
          newData.vintedUrl = urlObj.toString();
        } catch (e) {
          console.error('Erreur lors de la mise à jour de l\'URL:', e);
        }
      }

      // Si les couleurs changent, mettre à jour l'URL avec color_ids (sans [])
      if (field === 'selectedColors' && newData.vintedUrl) {
        const colors = value as string[];
        try {
          const urlObj = new URL(newData.vintedUrl);
          const params = new URLSearchParams(urlObj.search);

          // Supprimer tous les anciens paramètres color_ids[] et color_ids
          const paramsArray: [string, string][] = [];
          params.forEach((val, key) => {
            if (key !== 'color_ids[]' && key !== 'color_ids') {
              paramsArray.push([key, val]);
            }
          });

          // Convertir les titres de couleurs en IDs et ajouter les nouveaux paramètres color_ids (sans [])
          colors.forEach(colorTitle => {
            const colorObj = getColorByTitle(colorTitle);
            if (colorObj) {
              paramsArray.push(['color_ids', colorObj.id.toString()]);
            }
          });

          urlObj.search = new URLSearchParams(paramsArray).toString();
          newData.vintedUrl = urlObj.toString();
        } catch (e) {
          console.error('Erreur lors de la mise à jour de l\'URL:', e);
        }
      }

      // Si le prix minimum change, mettre à jour l'URL avec price_from et price_to
      if (field === 'minPrice' && newData.vintedUrl) {
        try {
          const urlObj = new URL(newData.vintedUrl);
          const params = new URLSearchParams(urlObj.search);

          // Supprimer les anciens paramètres price_from et price_to
          const paramsArray: [string, string][] = [];
          params.forEach((val, key) => {
            if (key !== 'price_from' && key !== 'price_to') {
              paramsArray.push([key, val]);
            }
          });

          // Ajouter les nouveaux paramètres
          const minPrice = value as string;
          if (minPrice && parseFloat(minPrice) > 0) {
            paramsArray.push(['price_from', minPrice]);
          }
          if (newData.maxPrice && parseFloat(newData.maxPrice) > 0) {
            paramsArray.push(['price_to', newData.maxPrice]);
          }

          urlObj.search = new URLSearchParams(paramsArray).toString();
          newData.vintedUrl = urlObj.toString();
        } catch (e) {
          console.error('Erreur lors de la mise à jour de l\'URL:', e);
        }
      }

      // Si le prix maximum change, mettre à jour l'URL avec price_from et price_to
      if (field === 'maxPrice' && newData.vintedUrl) {
        try {
          const urlObj = new URL(newData.vintedUrl);
          const params = new URLSearchParams(urlObj.search);

          // Supprimer les anciens paramètres price_from et price_to
          const paramsArray: [string, string][] = [];
          params.forEach((val, key) => {
            if (key !== 'price_from' && key !== 'price_to') {
              paramsArray.push([key, val]);
            }
          });

          // Ajouter les nouveaux paramètres
          if (newData.minPrice && parseFloat(newData.minPrice) > 0) {
            paramsArray.push(['price_from', newData.minPrice]);
          }
          const maxPrice = value as string;
          if (maxPrice && parseFloat(maxPrice) > 0) {
            paramsArray.push(['price_to', maxPrice]);
          }

          urlObj.search = new URLSearchParams(paramsArray).toString();
          newData.vintedUrl = urlObj.toString();
        } catch (e) {
          console.error('Erreur lors de la mise à jour de l\'URL:', e);
        }
      }

      return newData;
    });
  };

  const updateMultipleFields = (updates: Partial<AlertFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="h-full flex flex-col -m-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600">
            <X size={24} />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Modifier l'alerte</h2>
            <p className="text-sm text-gray-500">Configuration en temps réel</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          className="px-8 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl hover:from-cyan-600 hover:to-teal-600 transition-all font-bold shadow-lg shadow-cyan-200/50 hover:shadow-xl hover:shadow-cyan-200/60"
        >
          Enregistrer
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Filters (40%) */}
        <div className="w-[40%] border-r border-gray-200 bg-white overflow-y-auto p-8">
          {/* Badges récapitulatifs des filtres actifs */}
          <div className="mb-6">
            <FilterBadgesSummary
              formData={formData}
              onRemoveCondition={(condition) => updateField('selectedConditions', formData.selectedConditions.filter(c => c !== condition))}
              onRemoveSize={(size) => updateField('selectedSizes', formData.selectedSizes.filter(s => s.id !== size.id))}
              onRemoveColor={(color) => updateField('selectedColors', formData.selectedColors.filter(c => c !== color))}
              onRemoveBrand={(brand) => updateField('selectedBrands', formData.selectedBrands.filter(b => b.id !== brand.id))}
              onRemoveStatus={(statusId) => updateField('selectedStatuses', formData.selectedStatuses.filter(id => id !== statusId))}
              onRemovePlatform={(platform) => updateField('selectedPlatforms', (formData.selectedPlatforms || []).filter(p => p.id !== platform.id))}
              onRemoveRating={(rating) => updateField('selectedRatings', (formData.selectedRatings || []).filter(r => r.id !== rating.id))}
              onRemoveMaterial={(material) => updateField('selectedMaterials', (formData.selectedMaterials || []).filter(m => m.id !== material.id))}
              onRemovePattern={(pattern) => updateField('selectedPatterns', (formData.selectedPatterns || []).filter(p => p.id !== pattern.id))}
              onRemoveLanguage={(language) => updateField('selectedLanguages', (formData.selectedLanguages || []).filter(l => l.id !== language.id))}
              onRemoveInclude={(word) => updateField('mustInclude', formData.mustInclude.filter(w => w !== word))}
              onRemoveExclude={(word) => updateField('mustExclude', formData.mustExclude.filter(w => w !== word))}
              onClearPrice={() => { updateField('minPrice', ''); updateField('maxPrice', ''); }}
              onClearLikes={() => { updateField('minLikes', ''); updateField('maxLikes', ''); }}
            />
          </div>

          <div className="space-y-8">
            {/* Step 1: Name */}
            <div>
              <Step1Name
                value={formData.name}
                onChange={(value) => updateField('name', value)}
              />
            </div>

            {/* Step 2: URL */}
            <div>
              <Step2Url
                vintedUrl={formData.vintedUrl}
                searchText={formData.searchText || ''}
                onSearchTextChange={(text) => {
                  setFormData(prev => ({ ...prev, searchText: text, vintedUrl: '' }));
                }}
                onUrlChange={(url) => {
                  setFormData(prev => ({ ...prev, vintedUrl: url, searchText: '' }));
                }}
              />
            </div>

            {/* Step 3: Category */}
            <div>
              <Step3Category
                categoryId={formData.categoryId}
                categoryPath={formData.categoryPath}
                onCategorySelect={(id, path) => {
                  updateMultipleFields({ categoryId: id, categoryPath: path });
                }}
                onClearCategory={() => {
                  updateMultipleFields({ categoryId: '', categoryPath: '' });
                }}
              />
            </div>

            {/* Step 4: Category Filters */}
            <div>
              <Step4CategoryFilters
                categoryId={formData.categoryId}
                categoryPath={formData.categoryPath}
                selectedConditions={formData.selectedConditions}
                selectedSizes={formData.selectedSizes}
                selectedColors={formData.selectedColors}
                selectedBrands={formData.selectedBrands}
                selectedStatuses={formData.selectedStatuses}
                selectedLanguages={formData.selectedLanguages}
                selectedMaterials={formData.selectedMaterials}
                selectedPatterns={formData.selectedPatterns}
                selectedPlatforms={formData.selectedPlatforms}
                selectedRatings={formData.selectedRatings}
                onConditionsChange={(conditions) => updateField('selectedConditions', conditions)}
                onSizesChange={(sizes) => updateField('selectedSizes', sizes)}
                onColorsChange={(colors) => updateField('selectedColors', colors)}
                onBrandsChange={(brands) => updateField('selectedBrands', brands)}
                onStatusesChange={(statuses) => updateField('selectedStatuses', statuses)}
                onLanguagesChange={(languages) => updateField('selectedLanguages', languages)}
                onMaterialsChange={(materials) => updateField('selectedMaterials', materials)}
                onPatternsChange={(patterns) => updateField('selectedPatterns', patterns)}
                onPlatformsChange={(platforms) => updateField('selectedPlatforms', platforms)}
                onRatingsChange={(ratings) => updateField('selectedRatings', ratings)}
              />
            </div>

            {/* Step 5: General Options */}
            <div>
              <Step5GeneralOptions
                minPrice={formData.minPrice}
                maxPrice={formData.maxPrice}
                minLikes={formData.minLikes}
                maxLikes={formData.maxLikes}
                trendingOnly={formData.trendingOnly}
                maxPages={formData.maxPages}
                mustInclude={formData.mustInclude}
                mustExclude={formData.mustExclude}
                onFieldChange={(field, value) => updateField(field as keyof AlertFormData, value)}
                isUnlimited={isUnlimited}
                userPlan={userPlan}
              />
            </div>
          </div>
        </div>

        {/* Right Column - Images (60%) */}
        <div className="w-[60%] overflow-y-auto bg-gray-50 p-8">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Résultats ({results.length})</h3>
            {analysis && (
              <div className="flex items-center space-x-4">
                <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-100 text-center min-w-[80px]">
                  <span className="text-[10px] text-gray-500 block">Min</span>
                  <span className="text-sm font-bold text-green-600">{analysis.overall_stats.min}€</span>
                </div>
                <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-100 text-center min-w-[80px]">
                  <span className="text-[10px] text-gray-500 block">Moy</span>
                  <span className="text-sm font-bold text-cyan-600">{analysis.overall_stats.avg.toFixed(2)}€</span>
                </div>
                <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-100 text-center min-w-[80px]">
                  <span className="text-[10px] text-gray-500 block">Max</span>
                  <span className="text-sm font-bold text-gray-900">{analysis.overall_stats.max}€</span>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {results.map((result: any, idx: number) => (
              <a
                key={result.id || idx}
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-lg transition-all"
              >
                {result.image_url && (
                  <img src={result.image_url} alt={result.title} className="w-full aspect-square object-cover" />
                )}
                <div className="p-3">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-lg font-bold text-gray-900">{result.price}€</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">{result.title}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
